import React from 'react';
import PropTypes from 'prop-types';
import { useComposer } from '../../context/NewMessageComposerContext';
import ConversationListItem from './ConversationListItem';

/**
 * ConversationListPanel — Left panel with tab switcher and conversation list.
 *
 * Tabs: Messages | Requests
 * Messages tab: non-paid conversations (status: active, closed, waiting, failed)
 * Requests tab: isPaidRequest conversations
 */
function ConversationListPanel({
  conversations,
  activeTab,
  requestsBadgeCount = 0,
  onTabChange,
  selectedId,
  onSelect,
}) {
  const { openComposer } = useComposer();
  const filtered = conversations.filter((c) =>
    activeTab === 'messages'
      ? (c.tabType || (c.isPaidRequest ? 'requests' : 'messages')) === 'messages'
      : (c.tabType || (c.isPaidRequest ? 'requests' : 'messages')) === 'requests'
  );

  const renderEmptyState = () => {
    if (activeTab === 'messages') {
      return (
        <div className="msg-list-empty">
          <svg className="msg-list-empty__icon" width="64" height="64" viewBox="0 0 64 64" fill="none">
            <rect x="8" y="16" width="36" height="28" rx="6" stroke="currentColor" strokeWidth="2" />
            <rect x="20" y="24" width="36" height="28" rx="6" stroke="currentColor" strokeWidth="2" />
            <circle cx="38" cy="38" r="4" stroke="currentColor" strokeWidth="2" />
            <path d="M35 42 C35 42 36 44 38 44 C40 44 41 42 41 42" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <h3 className="msg-list-empty__title">No messages at this time</h3>
          <p className="msg-list-empty__text">
            When you receive a message, it will show up here.
          </p>
        </div>
      );
    }

    return (
      <div className="msg-list-empty">
        <svg className="msg-list-empty__icon" width="64" height="64" viewBox="0 0 64 64" fill="none">
          <rect x="12" y="18" width="40" height="30" rx="6" stroke="currentColor" strokeWidth="2" />
          <path d="M20 32h24M20 38h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <circle cx="44" cy="22" r="8" fill="currentColor" opacity="0.15" stroke="currentColor" strokeWidth="2" />
          <path d="M42 22h4M44 20v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <h3 className="msg-list-empty__title">No requests yet</h3>
        <p className="msg-list-empty__text">
          When someone pays to message you, it will appear here.
        </p>
      </div>
    );
  };

  return (
    <div className="msg-list-panel">
      <div className="msg-list-panel__header">
        <span className="msg-list-panel__header-title">Messages</span>
        <button
          type="button"
          className="msg-list-panel__new-msg-btn"
          onClick={openComposer}
          aria-label="New message"
          title="Start a new message"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>
      </div>

      <div className="msg-list-panel__tabs">
        <button
          type="button"
          className={`msg-list-panel__tab ${activeTab === 'messages' ? 'msg-list-panel__tab--active' : ''}`}
          onClick={() => onTabChange('messages')}
        >
          Messages
        </button>
        <button
          type="button"
          className={`msg-list-panel__tab ${activeTab === 'requests' ? 'msg-list-panel__tab--active' : ''}`}
          onClick={() => onTabChange('requests')}
        >
          Requests
          {requestsBadgeCount > 0 && (
            <span className="msg-list-panel__tab-badge">{requestsBadgeCount}</span>
          )}
        </button>
        <div
          className="msg-list-panel__tab-indicator"
          style={{ transform: activeTab === 'requests' ? 'translateX(100%)' : 'translateX(0)' }}
        />
      </div>

      <div className="msg-list-panel__list">
        {filtered.length === 0 ? (
          renderEmptyState()
        ) : (
          filtered.map((conv) => (
            <ConversationListItem
              key={conv.id}
              conversation={conv}
              isSelected={selectedId === conv.id}
              onClick={() => onSelect(conv.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

ConversationListPanel.propTypes = {
  conversations: PropTypes.array.isRequired,
  activeTab: PropTypes.oneOf(['messages', 'requests']).isRequired,
  requestsBadgeCount: PropTypes.number,
  onTabChange: PropTypes.func.isRequired,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

export default ConversationListPanel;
